import React, { useCallback, useEffect, useState } from 'react'
import {
  ArrowsPointingOutIcon,
  ClockIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'
import { deleteSession, listSessions, type AskEvansSession } from '../../api/askEvansApi'
import { ChatView } from './ChatView'

const iconBtn =
  'inline-flex h-8 w-8 items-center justify-center rounded-md text-[#8B93A1] transition hover:bg-[#F3F4F6] hover:text-[#3D4654] focus:outline-none focus-visible:ring-2 focus-visible:ring-hero-red/40'

function AskEvansSparkles({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M8.5 3.5l.85 2.55L12 6.9l-2.65.85L8.5 10.3l-.85-2.55L5 6.9l2.65-.85L8.5 3.5z" fill="#F26A7E" />
      <path d="M16.2 7.2l.7 2.1L19 10l-2.1.7-.7 2.1-.7-2.1L13.4 10l2.1-.7.7-2.1z" fill="#EB1C37" />
      <path d="M12.8 13.4l.95 2.85L16.6 17.2l-2.85.95-.95 2.85-.95-2.85L9.1 17.2l2.85-.95.95-2.85z" fill="#F48A9A" />
    </svg>
  )
}

function formatWhen(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

export function AskEvansFloatingPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const [sessions, setSessions] = useState<AskEvansSession[]>([])
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)

  const loadSessions = useCallback(() => {
    listSessions()
      .then((payload) => setSessions(payload.sessions))
      .catch(() => setSessions([]))
  }, [])

  useEffect(() => {
    if (!open) {
      setHistoryOpen(false)
      return
    }
    loadSessions()
  }, [open, loadSessions])

  if (!open) return null

  return (
    <>
      <div className="fixed inset-0 z-[68] bg-black/20 md:hidden" onClick={onClose} aria-hidden />
      <aside
        className="fixed bottom-[5.5rem] right-4 z-[69] flex h-[min(640px,calc(100vh-7.5rem))] w-[min(380px,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white shadow-[0_12px_40px_-8px_rgba(15,23,42,0.28)] sm:right-6"
        role="dialog"
        aria-label="Ask Evans"
      >
        <header className="relative z-20 flex shrink-0 items-start justify-between gap-2 border-b border-[#E5E7EB] bg-white px-4 pb-3 pt-3.5">
          <div className="min-w-0 pt-0.5">
            <div className="flex items-center gap-1.5">
              <AskEvansSparkles className="h-[18px] w-[18px] shrink-0" />
              <h2 className="font-sans text-[15px] font-semibold tracking-tight text-[#1F2937]">Ask Evans</h2>
              <span className="rounded-[4px] bg-[#F3F4F6] px-1.5 py-[1px] text-[9px] font-semibold uppercase tracking-[0.06em] text-[#9CA3AF]">
                BETA
              </span>
            </div>
            <p className="mt-0.5 pl-[22px] font-sans text-[11px] leading-snug text-[#9CA3AF]">
              Answers grounded in your WPS Studio data
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            <button type="button" className={iconBtn} aria-label="New chat" title="New chat" onClick={() => setSessionId(null)}>
              <PlusIcon className="h-[18px] w-[18px]" strokeWidth={1.75} />
            </button>
            <button
              type="button"
              className={`${iconBtn} ${historyOpen ? 'bg-[#F3F4F6] text-[#3D4654]' : ''}`}
              aria-label="Chat history"
              aria-expanded={historyOpen}
              onClick={() => setHistoryOpen((value) => !value)}
            >
              <ClockIcon className="h-[18px] w-[18px]" strokeWidth={1.75} />
            </button>
            <button
              type="button"
              className={iconBtn}
              aria-label="Open full screen"
              onClick={() => {
                onClose()
                navigate(sessionId ? `/ask-evans?session=${encodeURIComponent(sessionId)}` : '/ask-evans')
              }}
            >
              <ArrowsPointingOutIcon className="h-[18px] w-[18px]" strokeWidth={1.75} />
            </button>
            <button type="button" className={iconBtn} aria-label="Close Ask Evans" onClick={onClose}>
              <XMarkIcon className="h-[18px] w-[18px]" strokeWidth={1.75} />
            </button>
          </div>
          {historyOpen && (
            <div className="absolute left-3 right-3 top-full z-30 mt-1 max-h-64 overflow-hidden rounded-xl border border-[#E5E7EB] bg-white shadow-lg">
              <div className="border-b border-[#F3F4F6] px-3 py-2">
                <p className="font-sans text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF]">Recent chats</p>
              </div>
              <div className="max-h-52 overflow-y-auto py-1">
                {sessions.length === 0 ? (
                  <p className="px-3 py-4 text-center font-sans text-[12px] text-[#9CA3AF]">No chats yet. Ask a question to start.</p>
                ) : (
                  sessions.map((session) => (
                    <div key={session.id} className={`group flex items-center gap-1 px-1.5 ${session.id === sessionId ? 'bg-[#FEF2F2]' : ''}`}>
                      <button
                        type="button"
                        className="min-w-0 flex-1 rounded-lg px-2 py-2 text-left hover:bg-[#F9FAFB]"
                        onClick={() => {
                          setSessionId(session.id)
                          setHistoryOpen(false)
                        }}
                      >
                        <div className="truncate font-sans text-[12px] font-medium text-[#1F2937]">{session.title || 'Untitled chat'}</div>
                        <div className="mt-0.5 font-sans text-[10px] text-[#9CA3AF]">
                          {formatWhen(session.lastActiveAt)}
                          {typeof session.messageCount === 'number' ? ` · ${session.messageCount} messages` : ''}
                        </div>
                      </button>
                      <button
                        type="button"
                        aria-label="Delete chat"
                        className="mr-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[#9CA3AF] opacity-0 transition hover:bg-[#FEF2F2] hover:text-hero-red group-hover:opacity-100"
                        onClick={() => {
                          void deleteSession(session.id).then(() => {
                            setSessions((prev) => prev.filter((item) => item.id !== session.id))
                            if (sessionId === session.id) setSessionId(null)
                          })
                        }}
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </header>
        <div className="min-h-0 flex-1 bg-white">
          <ChatView
            variant="panel"
            sessionId={sessionId}
            onSessionChange={(id) => {
              setSessionId(id)
              loadSessions()
            }}
          />
        </div>
      </aside>
    </>
  )
}
